import { createSelector } from 'reselect';
import { RootState } from '../../../core/store';
import { selectSubscribedMsiVessels } from '../../../user-management/appSelectors';
import { TSubscribedMsiVessel } from '../../../user-management/subscription/subscriptionTypes';
import { ICohortVessel } from './cohortTypes';
import { FilterWrapper } from '../../filter/types/classes/FilterWrapper';
import { selectCohortSource } from './cohortSourceSlice';

const calculateMinValue = (dwt: number): number => {
	const factor = Math.pow(10, Math.round(Math.log10(0.2 * dwt)) - 1);
	return Math.floor((0.9 * dwt) / factor) * factor;
};

const calculateMaxValue = (dwt: number): number => {
	const factor = Math.pow(10, Math.round(Math.log10(0.2 * dwt)) - 1);
	return Math.ceil((1.1 * dwt) / factor) * factor;
};

const calculateIntervalRange = (minVal: number, maxVal: number): number => {
	const range = maxVal - minVal;
	return Math.pow(10, Math.round(Math.log10(range)) - 1);
};

const mapToICohortVessel = (v: TSubscribedMsiVessel): ICohortVessel => ({
	imoNumber: v.imoNumber,
	name: v.vesselName,
	size: v.size,
	sizeUnit: v.sizeUnit,
	age: v.age,
	yearOfDelivery: v.yearOfDelivery,
	msiControllingEntity: v.msiControllingEntity,
	gear: v.gear,
	scrubberFitted: v.scrubberFitted,
	fuelSystem: v.fuelSystem,
});

const deriveSizeRange = (cohortVessels: ICohortVessel[]) => {
	if (cohortVessels.length === 0) return { minSize: 0, maxSize: 0, rangeSize: 0 };
	const sizes = cohortVessels.map((v) => v.size);
	const minSize = Math.min(...sizes);
	const maxSize = Math.max(...sizes);
	const rangeSize = calculateIntervalRange(minSize, maxSize);
	return { minSize, maxSize, rangeSize };
};

const deriveAgeRange = (cohortVessels: ICohortVessel[]) => {
	if (cohortVessels.length === 0) return { minAge: 0, maxAge: 0 };
	const ages = cohortVessels.map((v) => v.age);
	const minAge = Math.min(...ages);
	const maxAge = Math.max(...ages);
	return { minAge, maxAge };
}

export const selectedCohortSourceVesselsSelector = createSelector(
	selectSubscribedMsiVessels,
	(state: RootState) => state.filterSlice.filters,
	(state: RootState) => state.portfolioSlice.portfolios,
	selectCohortSource,
	(_: RootState, imoNumber: number) => imoNumber,
	(subscribedMsiVessels, filters, portfolios, cohortSource, imoNumber) => {
		const { type, key } = cohortSource;

		if (type === 'auto') {
			const vessel = subscribedMsiVessels.find((v) => v.imoNumber === imoNumber);
			if (!vessel) return { cohortVessels: [] as ICohortVessel[], minSize: 0, maxSize: 0, rangeSize: 0, minAge: 0, maxAge: 0 };

			const minSize = calculateMinValue(vessel.size);
			const maxSize = calculateMaxValue(vessel.size);
			const rangeSize = calculateIntervalRange(minSize, maxSize);
			const minAge = vessel.age - 2;
			const maxAge = vessel.age + 2;

			const cohortVessels = subscribedMsiVessels
				.filter(
					(v) =>
						v.imoNumber !== vessel.imoNumber &&
						v.sectorId === vessel.sectorId &&
						v.size >= minSize &&
						v.size <= maxSize &&
						v.age >= minAge &&
						v.age <= maxAge,
				)
				.map(mapToICohortVessel);

			return { cohortVessels, minSize, maxSize, rangeSize, minAge, maxAge };
		}

		let cohortVessels: ICohortVessel[] = [];

		if (type === 'sector') {
			const sectorName = key.slice('sector-'.length);
			cohortVessels = subscribedMsiVessels.filter((v) => v.sectorName === sectorName).map(mapToICohortVessel);
		} else if (type === 'owner') {
			const ownerName = key.slice('owner-'.length);
			cohortVessels = subscribedMsiVessels.filter((v) => v.owner === ownerName).map(mapToICohortVessel);
		} else if (type === 'vessel') {
			const imoNum = parseInt(key.slice('vessel-'.length));
			cohortVessels = subscribedMsiVessels.filter((v) => v.imoNumber === imoNum).map(mapToICohortVessel);
		} else if (type === 'portfolio') {
			const portfolioId = parseInt(key.slice('portfolio-'.length));
			const portfolio = portfolios.find((p) => p.id === portfolioId);
			if (portfolio?.portfolioVesselsImoList?.length) {
				const imoSet = new Set(portfolio.portfolioVesselsImoList);
				cohortVessels = subscribedMsiVessels.filter((v) => imoSet.has(v.imoNumber)).map(mapToICohortVessel);
			}
		} else if (type === 'filter') {
			const filterId = parseInt(key.slice('filter-'.length));
			const filter = filters.find((f) => f.id === filterId);
			if (filter) {
				try {
					const wrapper = new FilterWrapper(filter.ownershipFilter || '[]', filter.sectorFilter || '[]');
					const filteredFormData = FilterWrapper.applyFilters(
						subscribedMsiVessels,
						wrapper.ownershipFilter.ownerships,
						wrapper.sectorFilter.sectors,
					);
					const filteredImos = new Set(filteredFormData.map((v) => v.imoNumber));
					cohortVessels = subscribedMsiVessels.filter((v) => filteredImos.has(v.imoNumber)).map(mapToICohortVessel);
				} catch {
					cohortVessels = [];
				}
			}
		}

		return { cohortVessels, ...deriveSizeRange(cohortVessels), ...deriveAgeRange(cohortVessels) };
	},
);
